import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { matrimonyApi } from '../matrimonyApi';

const STEPS = ['Account', 'Personal', 'Religion', 'Career', 'About You'];

const COMPLEXIONS    = ['Very Fair', 'Fair', 'Wheatish', 'Wheatish Brown', 'Dark'];
const BLOOD_GROUPS   = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];
const RELIGIONS      = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain', 'Other'];
const EDUCATIONS     = ['High School', 'Diploma', 'Bachelor\'s', 'Master\'s', 'PhD', 'Professional Degree', 'Other'];
const INCOMES        = ['Below 2 LPA', '2-5 LPA', '5-10 LPA', '10-20 LPA', '20-50 LPA', '50+ LPA'];
const FAMILY_TYPES   = ['Nuclear', 'Joint', 'Extended'];
const FAMILY_VALUES  = ['Traditional', 'Moderate', 'Liberal'];
const DIETS          = ['Vegetarian', 'Non-Vegetarian', 'Vegan', 'Eggetarian'];

export default function MatrimonyRegister() {
  const navigate = useNavigate();
  const [step, setStep]   = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoPreview, setPhotoPreview]     = useState('');
  const photoInputRef = useRef(null);

  const [data, setData] = useState({
    // Step 1
    name: '', email: '', phone: '', password: '', confirmPassword: '', gender: '',
    // Step 2
    date_of_birth: '', height_cm: '', weight_kg: '', complexion: '', blood_group: '',
    mother_tongue: '', city: '', state: '',
    // Step 3
    religion: '', caste: '', sub_caste: '',
    // Step 4
    education: '', education_field: '', occupation: '', employer: '', annual_income: '',
    // Step 5
    family_type: '', family_values: '', diet: '', about_me: '', profile_photo: '',
  });

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file));
    setPhotoUploading(true);
    setError('');
    try {
      const res = await matrimonyApi.uploadPhoto(file);
      set('profile_photo', res.url);
    } catch (err) {
      setError(err.message || 'Photo upload failed');
      setPhotoPreview('');
    } finally {
      setPhotoUploading(false);
    }
  };

  const validateStep = () => {
    setError('');
    if (step === 0) {
      if (!data.name.trim())    return setError('Name is required'), false;
      if (!data.email.trim())   return setError('Email is required'), false;
      if (!data.gender)         return setError('Please select Bride or Groom'), false;
      if (!data.password)       return setError('Password is required'), false;
      if (data.password.length < 6) return setError('Password must be at least 6 characters'), false;
      if (data.password !== data.confirmPassword) return setError('Passwords do not match'), false;
    }
    return true;
  };

  const nextStep = () => {
    if (!validateStep()) return;
    setStep(s => s + 1);
  };

  const prevStep = () => setStep(s => s - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    setLoading(true);
    setError('');
    try {
      const { confirmPassword, ...payload } = data;
      await matrimonyApi.register(payload);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="m-auth-wrap">
        <div className="m-auth-card" style={{ textAlign: 'center' }}>
          <div className="m-success-check">🎉</div>
          <h2 className="m-auth-title">Registration Submitted!</h2>
          <p style={{ color: 'var(--m-muted)', fontSize: 15, marginBottom: 20, lineHeight: 1.7 }}>
            Your profile has been submitted for review. Our team will approve it within 24–48 hours.
            Once approved, you'll be able to browse all profiles!
          </p>
          <div className="m-alert m-alert-warning" style={{ textAlign: 'left' }}>
            ⏳ Your account status is <strong>Pending Approval</strong>. You will be notified once approved.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 20 }}>
            <Link to="/login" className="m-btn m-btn-primary">Login to Account</Link>
            <Link to="/"      className="m-btn m-btn-ghost">Go to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="m-auth-wrap" style={{ alignItems: 'flex-start', paddingTop: 40 }}>
      <div style={{ width: '100%', maxWidth: 560, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <span style={{ fontSize: 40 }}>💑</span>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 800, marginTop: 8 }}>
            Create Your Profile
          </h1>
          <p style={{ color: 'var(--m-muted)', marginTop: 4 }}>
            Step {step + 1} of {STEPS.length}: <strong>{STEPS[step]}</strong>
          </p>
        </div>

        {/* Step indicator */}
        <div className="m-step-indicator">
          {STEPS.map((label, i) => (
            <div key={i} className={`m-step${i === step ? ' active' : i < step ? ' completed' : ''}`}>
              <div className="m-step-circle">{i < step ? '✓' : i + 1}</div>
              <div className="m-step-label">{label}</div>
            </div>
          ))}
        </div>

        <div className="m-card">
          <div className="m-card-body">
            {error && <div className="m-alert m-alert-error">{error}</div>}

            <form onSubmit={step === STEPS.length - 1 ? handleSubmit : e => { e.preventDefault(); nextStep(); }}>
              {/* Step 0: Account */}
              {step === 0 && (
                <>
                  <div className="m-form-group">
                    <label className="m-form-label">Full Name <span className="required">*</span></label>
                    <input className="m-form-control" placeholder="Your full name" value={data.name} onChange={e => set('name', e.target.value)} required />
                  </div>
                  <div className="m-form-group">
                    <label className="m-form-label">Email Address <span className="required">*</span></label>
                    <input className="m-form-control" type="email" placeholder="your@email.com" value={data.email} onChange={e => set('email', e.target.value)} required />
                  </div>
                  <div className="m-form-group">
                    <label className="m-form-label">Phone Number</label>
                    <input className="m-form-control" type="tel" placeholder="+91 98765 43210" value={data.phone} onChange={e => set('phone', e.target.value)} />
                  </div>
                  <div className="m-form-group">
                    <label className="m-form-label">I am a <span className="required">*</span></label>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className={`m-btn m-btn-sm${data.gender === 'bride' ? ' m-btn-primary' : ' m-btn-ghost'}`}
                        style={{ flex: 1 }}
                        onClick={() => set('gender', 'bride')}
                      >
                        👰 Bride
                      </button>
                      <button
                        type="button"
                        className={`m-btn m-btn-sm${data.gender === 'groom' ? ' m-btn-primary' : ' m-btn-ghost'}`}
                        style={{ flex: 1 }}
                        onClick={() => set('gender', 'groom')}
                      >
                        🤵 Groom
                      </button>
                    </div>
                  </div>
                  <div className="m-form-row">
                    <div className="m-form-group">
                      <label className="m-form-label">Password <span className="required">*</span></label>
                      <input className="m-form-control" type="password" placeholder="Min 6 characters" value={data.password} onChange={e => set('password', e.target.value)} required />
                    </div>
                    <div className="m-form-group">
                      <label className="m-form-label">Confirm Password <span className="required">*</span></label>
                      <input className="m-form-control" type="password" placeholder="Repeat password" value={data.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} required />
                    </div>
                  </div>
                </>
              )}

              {/* Step 1: Personal */}
              {step === 1 && (
                <>
                  <div className="m-form-row">
                    <div className="m-form-group">
                      <label className="m-form-label">Date of Birth</label>
                      <input className="m-form-control" type="date" value={data.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} />
                    </div>
                    <div className="m-form-group">
                      <label className="m-form-label">Blood Group</label>
                      <select className="m-form-control" value={data.blood_group} onChange={e => set('blood_group', e.target.value)}>
                        <option value="">Select</option>
                        {BLOOD_GROUPS.map(b => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="m-form-row">
                    <div className="m-form-group">
                      <label className="m-form-label">Height (cm)</label>
                      <input className="m-form-control" type="number" placeholder="165" min="100" max="220" value={data.height_cm} onChange={e => set('height_cm', e.target.value)} />
                    </div>
                    <div className="m-form-group">
                      <label className="m-form-label">Weight (kg)</label>
                      <input className="m-form-control" type="number" placeholder="60" min="30" max="200" value={data.weight_kg} onChange={e => set('weight_kg', e.target.value)} />
                    </div>
                  </div>
                  <div className="m-form-row">
                    <div className="m-form-group">
                      <label className="m-form-label">Complexion</label>
                      <select className="m-form-control" value={data.complexion} onChange={e => set('complexion', e.target.value)}>
                        <option value="">Select</option>
                        {COMPLEXIONS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="m-form-group">
                      <label className="m-form-label">Mother Tongue</label>
                      <input className="m-form-control" placeholder="e.g. Telugu" value={data.mother_tongue} onChange={e => set('mother_tongue', e.target.value)} />
                    </div>
                  </div>
                  <div className="m-form-row">
                    <div className="m-form-group">
                      <label className="m-form-label">City</label>
                      <input className="m-form-control" placeholder="Your city" value={data.city} onChange={e => set('city', e.target.value)} />
                    </div>
                    <div className="m-form-group">
                      <label className="m-form-label">State</label>
                      <input className="m-form-control" placeholder="Your state" value={data.state} onChange={e => set('state', e.target.value)} />
                    </div>
                  </div>
                </>
              )}

              {/* Step 2: Religion */}
              {step === 2 && (
                <>
                  <div className="m-form-group">
                    <label className="m-form-label">Religion</label>
                    <select className="m-form-control" value={data.religion} onChange={e => set('religion', e.target.value)}>
                      <option value="">Select Religion</option>
                      {RELIGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="m-form-group">
                    <label className="m-form-label">Caste</label>
                    <input className="m-form-control" placeholder="e.g. Brahmin, Kshatriya" value={data.caste} onChange={e => set('caste', e.target.value)} />
                  </div>
                  <div className="m-form-group">
                    <label className="m-form-label">Sub Caste</label>
                    <input className="m-form-control" placeholder="Optional" value={data.sub_caste} onChange={e => set('sub_caste', e.target.value)} />
                  </div>
                </>
              )}

              {/* Step 3: Education & Career */}
              {step === 3 && (
                <>
                  <div className="m-form-row">
                    <div className="m-form-group">
                      <label className="m-form-label">Highest Education</label>
                      <select className="m-form-control" value={data.education} onChange={e => set('education', e.target.value)}>
                        <option value="">Select</option>
                        {EDUCATIONS.map(e => <option key={e} value={e}>{e}</option>)}
                      </select>
                    </div>
                    <div className="m-form-group">
                      <label className="m-form-label">Field of Study</label>
                      <input className="m-form-control" placeholder="e.g. Engineering, Medicine" value={data.education_field} onChange={e => set('education_field', e.target.value)} />
                    </div>
                  </div>
                  <div className="m-form-group">
                    <label className="m-form-label">Occupation</label>
                    <input className="m-form-control" placeholder="Your job title or profession" value={data.occupation} onChange={e => set('occupation', e.target.value)} />
                  </div>
                  <div className="m-form-group">
                    <label className="m-form-label">Employer / Company</label>
                    <input className="m-form-control" placeholder="Where do you work?" value={data.employer} onChange={e => set('employer', e.target.value)} />
                  </div>
                  <div className="m-form-group">
                    <label className="m-form-label">Annual Income</label>
                    <select className="m-form-control" value={data.annual_income} onChange={e => set('annual_income', e.target.value)}>
                      <option value="">Select income range</option>
                      {INCOMES.map(i => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                </>
              )}

              {/* Step 4: About You */}
              {step === 4 && (
                <>
                  <div className="m-form-row">
                    <div className="m-form-group">
                      <label className="m-form-label">Family Type</label>
                      <select className="m-form-control" value={data.family_type} onChange={e => set('family_type', e.target.value)}>
                        <option value="">Select</option>
                        {FAMILY_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                    <div className="m-form-group">
                      <label className="m-form-label">Family Values</label>
                      <select className="m-form-control" value={data.family_values} onChange={e => set('family_values', e.target.value)}>
                        <option value="">Select</option>
                        {FAMILY_VALUES.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="m-form-group">
                    <label className="m-form-label">Diet</label>
                    <select className="m-form-control" value={data.diet} onChange={e => set('diet', e.target.value)}>
                      <option value="">Select</option>
                      {DIETS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="m-form-group">
                    <label className="m-form-label">About Me</label>
                    <textarea
                      className="m-form-control"
                      placeholder="Tell potential matches about yourself — your personality, interests, what you're looking for…"
                      rows={4}
                      value={data.about_me}
                      onChange={e => set('about_me', e.target.value)}
                    />
                  </div>
                  <div className="m-form-group">
                    <label className="m-form-label">Profile Photo (optional)</label>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      style={{ display: 'none' }}
                      onChange={handlePhotoChange}
                    />
                    <div className="m-upload-row" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      {(photoPreview || data.profile_photo) && (
                        <img
                          src={photoPreview || data.profile_photo}
                          alt="Preview"
                          style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--m-primary)' }}
                        />
                      )}
                      <button
                        type="button"
                        className="m-btn m-btn-outline m-btn-sm"
                        onClick={() => photoInputRef.current && photoInputRef.current.click()}
                        disabled={photoUploading}
                        style={{ minWidth: 140 }}
                      >
                        {photoUploading ? '⏳ Uploading…' : data.profile_photo ? '🔄 Change Photo' : '📷 Upload Photo'}
                      </button>
                    </div>
                    {data.profile_photo && !photoUploading && (
                      <div className="m-form-hint" style={{ color: 'var(--m-success, #2e7d32)', marginTop: 6 }}>
                        ✓ Photo uploaded successfully
                      </div>
                    )}
                    <div className="m-form-hint">Max 5 MB · JPG, PNG, WebP accepted. You can also add this later from your dashboard.</div>
                  </div>
                </>
              )}

              {/* Navigation */}
              <div className="m-form-actions" style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                {step > 0 && (
                  <button type="button" className="m-btn m-btn-ghost" onClick={prevStep}>
                    ← Back
                  </button>
                )}
                {step < STEPS.length - 1 && (
                  <button type="submit" className="m-btn m-btn-primary" style={{ flex: 1 }}>
                    Next: {STEPS[step + 1]} →
                  </button>
                )}
                {step === STEPS.length - 1 && (
                  <button type="submit" className="m-btn m-btn-gold" style={{ flex: 1 }} disabled={loading}>
                    {loading ? 'Submitting…' : '🌹 Submit Registration'}
                  </button>
                )}
              </div>
            </form>

            {step === 0 && (
              <div className="m-divider-text" style={{ marginTop: 20 }}>
                <span>Already registered?</span>
              </div>
            )}
            {step === 0 && (
              <div style={{ textAlign: 'center' }}>
                <Link to="/login" className="m-btn m-btn-outline m-btn-full">Login to Your Account</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
